import { Component, HostBinding, AfterViewInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { ScreenService, AppInfoService, AuthService } from './shared/services';
import { SignalRService } from './shared/services/signal-r.service';
import { FeatureFlagsService } from './shared/services/feature-flags.service';
import { locale } from 'devextreme/localization'
import config from 'devextreme/core/config';
import { licenseKey } from './devextreme-license';
import { Router, NavigationEnd } from '@angular/router';
import { TokenMonitorService } from './shared/services/token-monitor.service';
import { NOTIFICATION_STORE } from './shared/stores';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements AfterViewInit, OnDestroy {
  private readonly notificationStore = inject(NOTIFICATION_STORE);

  @HostBinding('class') get getClass() {
    return Object.keys(this.screen.sizes).filter(cl => this.screen.sizes[cl]).join(' ');
  }

  appLocale: string
  isLoggedIn: boolean | undefined = undefined; // undefined = loading state
  isReady: boolean | null = null; // null = not initialized, false = initializing, true = ready
  private isScrollingSyncing = false;
  private resizeTimeout: any;
  // Elements already accounted for by the content mutation observer. Used to tell a
  // genuinely new grid being rendered apart from unrelated DOM churn (e.g. a drawer,
  // dialog or form opening) that merely happens to match the observer's selectors.
  private readonly trackedGridElements = new WeakSet<Element>();
  private contentMutationObserver?: MutationObserver;

  constructor(
    private readonly authService: AuthService,
    private readonly screen: ScreenService,
    private readonly signalRService: SignalRService,
    private readonly tokenMonitorService: TokenMonitorService,
    private readonly cdr: ChangeDetectorRef,
    private readonly featureFlagsService: FeatureFlagsService,
    public appInfo: AppInfoService,
    public router: Router)
  {
      locale("en-US");
      this.appLocale = "en-US";
      config({licenseKey});

      // Initialize authentication synchronously to prevent any rendering before state is set
      // APP_INITIALIZER has already warmed the cache, so this should be instant
      // console.log('🏗️ Constructor: Getting cached auth state...');
      this.authService.isAuthenticatedAsync().then(isAuth => {
        this.isLoggedIn = isAuth;
        this.isReady = true; // Mark as ready to render
        // console.log('🏗️ Constructor: Set isLoggedIn =', this.isLoggedIn, ', isReady =', this.isReady);
        this.cdr.detectChanges();
      });
  }

  /**
   * Check if the current route is an authentication callback route
   * These routes need to be accessible even when not authenticated
   */
  isAuthCallbackRoute(): boolean {
    const url = this.router.url;
    const isCallback = url.startsWith('/signin-callback') || url.startsWith('/signout-callback');
    // console.log('🔍 isAuthCallbackRoute check:', { url, isCallback });
    return isCallback;
  }

  async ngOnInit() {
    // Initialize SignalR service (self-managing from this point)
    this.signalRService.initialize();

    // CRITICAL: Set up authentication state change subscription FIRST
    // This ensures we catch login events from completeLogin() during signin-callback
    this.authService.loginChanged.subscribe(async (loggedIn) => {
      // console.log('🔔 loginChanged event received:', loggedIn);
      this.isLoggedIn = loggedIn;

      // Manually trigger change detection to ensure UI updates
      this.cdr.detectChanges();

      if (loggedIn) {
        // User logged in - start token monitoring
        this.tokenMonitorService.startMonitoring();
        // Load feature flags now that an access token is available. The
        // APP_INITIALIZER skips loading when no token is present at bootstrap
        // (e.g. fresh login redirect flow); this catches that case so the flags
        // are warmed before the user navigates further. `load()` is idempotent.
        this.featureFlagsService.load().catch(() => { /* falls back to defaults */ });
      } else if (!this.authService.isLoggingIn()) {
        // User logged out - stop token monitoring and redirect
        this.tokenMonitorService.stopMonitoring();
        this.clearApplicationData();
        this.router.navigate(['login']);
      }
    });

    // Check authentication state (should already be initialized by APP_INITIALIZER)
    const isAuthenticated = await this.authService.isAuthenticatedAsync();

    // Only update if state actually changed to avoid duplicate actions
    if (this.isLoggedIn !== isAuthenticated) {
      this.isLoggedIn = isAuthenticated;
      this.cdr.detectChanges();
    }

    // Start token monitoring if user is authenticated
    if (this.isLoggedIn) {
      this.tokenMonitorService.startMonitoring();
    }
  }

  ngAfterViewInit(): void {
    // Initialize scrollbar system
    const globalScrollbar = document.getElementById('globalScrollbar');
    if (globalScrollbar) {
      globalScrollbar.style.display = 'none';
      globalScrollbar.scrollLeft = 0;

      // Add scroll synchronization
      globalScrollbar.addEventListener('scroll', () => {
        if (!this.isScrollingSyncing) {
          this.isScrollingSyncing = true;
          this.syncDataGridScrolls(globalScrollbar.scrollLeft);
          setTimeout(() => { this.isScrollingSyncing = false; }, 10);
        }
      });
    }

    // Add DataGrid scroll listeners for reverse sync
    this.setupDataGridScrollListeners();

    // Window resize listener with debouncing
    window.addEventListener('resize', () => {
      // Clear previous timeout
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        // Force DOM updates and wait for containers to recalculate
        setTimeout(() => {
          this.ultraSimpleScrollbarCheck();
        }, 100);
      }, 300);
    });

    // Route change listener
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (globalScrollbar) {
          globalScrollbar.style.display = 'none';
          globalScrollbar.scrollLeft = 0;
          const _ = globalScrollbar.offsetWidth; // Force reflow
          globalScrollbar.scrollLeft = 0;
        }
        setTimeout(() => {
          this.ultraSimpleScrollbarCheck();
        }, 500);
      }
    });

    // Content mutation observer
    this.setupContentMutationObserver();

    this.initializeDevExtremeTheme();
  }

  ngOnDestroy(): void {
    // Clear resize timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    // Disconnect the content mutation observer to avoid leaking observers/listeners
    this.contentMutationObserver?.disconnect();

    // Stop token monitoring to clean up resources
    this.tokenMonitorService.stopMonitoring();
  }

  private async initializeAuthenticationState(): Promise<void> {
    // console.log('🔍 initializeAuthenticationState: START - isLoggedIn:', this.isLoggedIn);
    try {
      // Use OIDC-based authentication check
      // console.log('🔐 Checking authentication with authService.isAuthenticatedAsync()');
      const isAuthenticated = await this.authService.isAuthenticatedAsync();
      // console.log('🔐 Authentication check result:', isAuthenticated);
      this.isLoggedIn = isAuthenticated;
      // console.log('✅ Set isLoggedIn =', this.isLoggedIn);
    } catch (error) {
      // console.error('❌ Authentication check failed:', error);
      this.isLoggedIn = false;
    }
    // console.log('🔍 initializeAuthenticationState: END - isLoggedIn:', this.isLoggedIn);
  }

  private clearApplicationData(): void {
    // Clear only non-OIDC data to avoid disrupting authentication
    const keysToPreserve = [
      'oidc.user:', // OIDC user data
      'oidc.', // Other OIDC state
      'Tasks_Grid', // Grid layout preferences (column order, width, visibility) — persisted across sessions
    ];

    // Get all localStorage keys
    const allKeys = Object.keys(localStorage);

    // Clear only keys that don't match OIDC patterns
    allKeys.forEach(key => {
      const shouldPreserve = keysToPreserve.some(prefix => key.startsWith(prefix));
      if (!shouldPreserve) {
        localStorage.removeItem(key);
      }
    });

    // Clear sessionStorage completely (it shouldn't contain OIDC state)
    sessionStorage.clear();
  }

  private setupContentMutationObserver() {
    const observer = new MutationObserver((mutations) => {
      const addedElements: Element[] = [];

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              addedElements.push(node as Element);
            }
          });
        }
      });

      if (addedElements.length === 0) {
        return;
      }

      const { hasNewGrid, hasUnrelatedLayoutChange } = this.classifyContentMutations(addedElements);

      if (hasNewGrid) {
        // A grid genuinely appeared for the first time (or was re-rendered from
        // scratch) - its own scroll position starts at 0, so recalculating and
        // resetting the shared scrollbar here matches existing behavior and is safe.
        setTimeout(() => {
          this.ultraSimpleScrollbarCheck();
        }, 200);
      } else if (hasUnrelatedLayoutChange) {
        // Some other DOM change occurred nearby (e.g. a drawer, dialog or form
        // opening) that may affect whether the shared scrollbar needs to be shown,
        // hidden or resized, but it must NOT snap any grid's active horizontal
        // scroll position back to the first column.
        setTimeout(() => {
          this.ultraSimpleScrollbarCheck(true);
        }, 200);
      }
    });

    const contentArea = document.querySelector('router-outlet')?.parentElement || document.body;
    observer.observe(contentArea, {
      childList: true,
      subtree: true,
      attributes: false
    });

    this.contentMutationObserver = observer;
  }

  /**
   * Classifies a batch of newly-added DOM nodes reported by the content mutation
   * observer, distinguishing genuinely new grids (which legitimately need the
   * shared scrollbar recalculated/reset) from other layout-affecting mutations
   * such as a drawer/dialog/form opening (which must not reset scroll position).
   */
  private classifyContentMutations(addedElements: Element[]): { hasNewGrid: boolean; hasUnrelatedLayoutChange: boolean } {
    const gridSelector = 'app-data-grid, .dx-datagrid';
    let hasNewGrid = false;
    let hasUnrelatedLayoutChange = false;

    addedElements.forEach((element) => {
      const candidateGrids: Element[] = [];
      if (typeof element.matches === 'function' && element.matches(gridSelector)) {
        candidateGrids.push(element);
      }
      if (typeof element.querySelectorAll === 'function') {
        candidateGrids.push(...Array.from(element.querySelectorAll(gridSelector)));
      }

      let elementIntroducedNewGrid = false;
      candidateGrids.forEach((gridElement) => {
        if (!this.trackedGridElements.has(gridElement)) {
          this.trackedGridElements.add(gridElement);
          hasNewGrid = true;
          elementIntroducedNewGrid = true;
        }
      });

      const touchesForm =
        (typeof element.matches === 'function' && element.matches('.dx-form')) ||
        (typeof element.querySelector === 'function' && !!element.querySelector('.dx-form'));

      if (touchesForm && !elementIntroducedNewGrid) {
        hasUnrelatedLayoutChange = true;
      }
    });

    return { hasNewGrid, hasUnrelatedLayoutChange };
  }

  // Public method for external components to trigger scrollbar updates
  public updateScrollbarVisibility(): void {
    setTimeout(() => {
      this.ultraSimpleScrollbarCheck();
    }, 100);
  }

  private ultraSimpleScrollbarCheck(preserveScrollPosition: boolean = false): void {
    const globalScrollbar = document.getElementById('globalScrollbar');
    if (!globalScrollbar) return;

    // Preserve current scroll position if requested
    const currentScrollLeft = preserveScrollPosition ? globalScrollbar.scrollLeft : 0;

    // Always reset to zero first (unless preserving)
    if (!preserveScrollPosition) {
      globalScrollbar.scrollLeft = 0;
    }

    // Check if any container needs scrolling
    const containers = document.querySelectorAll('.dx-scrollable-container, .dx-datagrid-scroll-container');
    let needsScrollbar = false;

    containers.forEach(container => {
      const el = container as HTMLElement;
      if (el.scrollWidth > el.clientWidth + 10) {
        needsScrollbar = true;
      }
    });

    if (needsScrollbar) {
      globalScrollbar.style.display = 'block';

      // Update scrollbar content width
      this.updateScrollbarContent();

      if (preserveScrollPosition) {
        // Restore preserved scroll position
        globalScrollbar.scrollLeft = currentScrollLeft;
      } else {
        // Force zero position after showing
        globalScrollbar.scrollLeft = 0;
        const _ = globalScrollbar.offsetWidth; // Force reflow
        globalScrollbar.scrollLeft = 0;
      }
    } else {
      globalScrollbar.style.display = 'none';
      globalScrollbar.scrollLeft = 0;
    }
  }

  private syncDataGridScrolls(scrollLeft: number): void {
    // Sync all DataGrid horizontal scrolls
    const containers = document.querySelectorAll('.dx-scrollable-container, .dx-datagrid-scroll-container');
    containers.forEach(container => {
      const el = container as HTMLElement;
      if (el.scrollWidth > el.clientWidth + 10) {
        el.scrollLeft = scrollLeft;
      }
    });
  }

  private setupDataGridScrollListeners(): void {
    // Use event delegation to handle dynamically added DataGrids
    document.addEventListener('scroll', (event) => {
      const target = event.target as HTMLElement;
      if (!this.isScrollingSyncing && target && target.classList && (
        target.classList.contains('dx-scrollable-container') ||
        target.classList.contains('dx-datagrid-scroll-container')
      )) {
        // Only sync if this is actually a horizontal scroll change
        // and the element has horizontal scrolling capability
        if (target.scrollWidth > target.clientWidth + 10) {
          const globalScrollbar = document.getElementById('globalScrollbar');
          if (globalScrollbar && target.scrollLeft !== globalScrollbar.scrollLeft) {
            this.isScrollingSyncing = true;
            globalScrollbar.scrollLeft = target.scrollLeft;
            setTimeout(() => { this.isScrollingSyncing = false; }, 10);
          }
        }
      }
    }, true); // Use capture phase to ensure we catch all scroll events
  }

  private updateScrollbarContent(): void {
    const globalScrollbar = document.getElementById('globalScrollbar');
    const scrollbarContent = document.getElementById('scrollbarContent');
    if (!globalScrollbar || !scrollbarContent) return;

    // Force DOM reflow to ensure accurate measurements
    const _ = document.body.offsetHeight;

    // Find the container with the largest scroll requirement
    const containers = document.querySelectorAll('.dx-scrollable-container, .dx-datagrid-scroll-container');
    let targetContainer: HTMLElement | null = null;
    let maxScrollRatio = 0;

    containers.forEach(container => {
      const el = container as HTMLElement;
      // Force element reflow
      const __ = el.offsetWidth;

      if (el.scrollWidth > el.clientWidth + 10) {
        const scrollRatio = el.scrollWidth / el.clientWidth;
        if (scrollRatio > maxScrollRatio) {
          maxScrollRatio = scrollRatio;
          targetContainer = el;
        }
      }
    });

    if (targetContainer) {
      // Calculate the scrollbar content width to match the DataGrid's scroll proportions
      const globalScrollbarWidth = globalScrollbar.clientWidth;
      const requiredContentWidth = globalScrollbarWidth * (targetContainer.scrollWidth / targetContainer.clientWidth);
      scrollbarContent.style.width = Math.ceil(requiredContentWidth) + 'px';
    }
  }

  private initializeDevExtremeTheme(): void {
    // DevExtreme theme initialization
  }
}
